import { useState, useEffect } from "react";
import {
  useFlows,
  FlowCondition,
  FlowEmailAction,
  FlowTemplateAction,
} from "../../../../../stores/flowsStore";

export type ActionType = "email" | "template" | null;

export const useAutomationFlowEditor = () => {
  const {
    setSubView,
    fetchDataSources,
    createFlow,
    updateFlow,
    testFlow,
    editingFlow,
    setEditingFlow,
    error,
    clearError,
  } = useFlows();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [conditions, setConditions] = useState<FlowCondition[]>([]);
  const [conditionsLogic, setConditionsLogic] = useState("AND");
  const [emailAction, setEmailAction] = useState<FlowEmailAction | null>(null);
  const [templateAction, setTemplateAction] = useState<FlowTemplateAction | null>(null);
  const [activeActions, setActiveActions] = useState<ActionType[]>([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    conditions_met: boolean;
    simulated_actions: Array<{ action: string; would_execute: boolean }>;
  } | null>(null);

  const isEditing = !!editingFlow?.identifier;

  useEffect(() => {
    fetchDataSources();
  }, [fetchDataSources]);

  useEffect(() => {
    if (editingFlow) {
      setName(editingFlow.name || "");
      setDescription(editingFlow.description || "");
      setConditions(editingFlow.conditions || []);
      setConditionsLogic(editingFlow.conditions_logic || "AND");
      setEmailAction(editingFlow.email_action || null);
      setTemplateAction(editingFlow.template_action || null);

      const actions: ActionType[] = [];
      if (editingFlow.template_action) actions.push("template");
      if (editingFlow.email_action) actions.push("email");
      setActiveActions(actions.length > 0 ? actions : []);
    }
  }, [editingFlow]);

  const handleConditionsChange = (newConditions: FlowCondition[], logic: string) => {
    setConditions(newConditions);
    setConditionsLogic(logic);
  };

  const handleBack = () => {
    setEditingFlow(null);
    setSubView("menu");
  };

  const handleAddTemplate = () => {
    if (!activeActions.includes("template")) {
      setActiveActions([...activeActions, "template"]);
      setTemplateAction({ template_id: "", template_name: "" } as FlowTemplateAction);
    }
  };

  const handleAddEmail = () => {
    if (!activeActions.includes("email")) {
      setActiveActions([...activeActions, "email"]);
      setEmailAction({ to: "", subject: "", body: "" });
    }
  };

  const handleRemoveAction = (actionType: ActionType) => {
    setActiveActions(activeActions.filter((a) => a !== actionType));
    if (actionType === "email") setEmailAction(null);
    if (actionType === "template") setTemplateAction(null);
  };

  const handleSave = async () => {
    setSaving(true);
    clearError();

    const flowData = {
      name: name.trim(),
      description: description.trim() || undefined,
      conditions,
      conditions_logic: conditionsLogic,
      email_action: emailAction || undefined,
      template_action: templateAction || undefined,
      status: "draft" as const,
    };

    try {
      if (isEditing && editingFlow?.identifier) {
        await updateFlow(editingFlow.identifier, flowData);
      } else {
        await createFlow(flowData);
      }
      handleBack();
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!isEditing || !editingFlow?.identifier) {
      return;
    }

    setTesting(true);
    setTestResult(null);

    const result = await testFlow(editingFlow.identifier, {});
    setTestResult(result);
    setTesting(false);
  };

  return {
    name,
    setName,
    description,
    setDescription,
    conditions,
    conditionsLogic,
    emailAction,
    setEmailAction,
    templateAction,
    setTemplateAction,
    activeActions,
    saving,
    testing,
    testResult,
    setTestResult,
    isEditing,
    error,
    clearError,
    handleConditionsChange,
    handleBack,
    handleAddTemplate,
    handleAddEmail,
    handleRemoveAction,
    handleSave,
    handleTest,
  };
};
